import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertTriangle, Wrench, Layers, Shield, Thermometer, Search } from "lucide-react";
import heroImage from "@/assets/stone-masonry-steps-verona-essex-county-nj-2.webp";
import chimneyRepairWayne from "@/assets/fireplace-stone-masonry-repair-wayne-passaic-county-nj.webp";
import stoneMasonryRepairNutley from "@/assets/services/stone-masonry-repair-nutley-nj.webp";
import stoneMasonryTotowa from "@/assets/stone-masonry-totowa-passaic-nj.webp";
import stoneWorkTotowa from "@/assets/stone-work-totowa-nj.webp";


const CitationLink = ({ num, url, children }: { num: number; url: string; children: React.ReactNode }) => (
  <>
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">{children}</a>
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline"><sup>[{num}]</sup></a>
  </>
);

const contentSections: Array<{
  icon: React.ElementType;
  title: string;
  image?: string;
  imageAlt?: string;
  isCallout?: boolean;
  calloutItems?: string[];
  content: string[];
  citation: { num: number; url: string; text: string };
}> = [
  {
    icon: AlertTriangle,
    title: "Crumbling Mortar, Cracked Stone, and Spalling Are Signs Your Stonework Needs Repair",
    image: heroImage,
    imageAlt: "Stone masonry steps with flagstone treads in Verona, Essex County, NJ, by Masonry Pro Construction",
    content: [
      "Notice white powder on your stone wall? That is efflorescence. It means moisture is moving through the masonry and pulling minerals to the surface. On its own, it is mostly cosmetic. But paired with cracked mortar joints or loose stones, it points to a bigger issue.",
      "North Jersey gets 80 or more freeze-thaw cycles in a typical winter. Water gets into a joint, freezes, expands, and cracks the mortar from the inside out. Do that 80 times and the damage adds up fast. Bergen County homes built with local fieldstone or brownstone are especially prone to this because those materials are softer than granite or quartzite.",
      "Spalling is another one to watch for. That is when the face of a stone flakes off in layers. It usually means water got trapped behind the surface and froze. Once spalling starts, the exposed stone underneath wears down even faster.",
      "Catching any of these early keeps the repair small. Wait too long and a few bad joints turn into a section that needs to come apart and be rebuilt."
    ],
    citation: { num: 1, url: "https://www.nps.gov/orgs/1739/upload/preservation-brief-02-repointing.pdf", text: "Preservation Brief 2 (U.S. National Park Service)" }
  },
  {
    icon: Wrench,
    title: "A Skilled Masonry Contractor Handles Stone Repairs a Handyman Cannot",
    image: chimneyRepairWayne,
    imageAlt: "Stone masonry chimney and roof on a house in Wayne, Passaic County, NJ, with a golf course in the background, Masonry Pro",
    content: [
      "Stone repair is not just about filling cracks. The mortar needs to match the original in strength, flexibility, and color. Using the wrong mix can damage the surrounding stone. A Type S mortar on a soft brownstone wall, for example, will cause the stone to crack before the mortar does. That is the opposite of what you want.",
      "Homeowners in Passaic and Morris Counties sometimes call a general handyman for this kind of work. That can be OK for a small cosmetic patch, but anything structural, like a leaning wall, a cracked chimney, or stone steps pulling away from a foundation, will need someone who understands load paths and mortar chemistry.",
      "New Jersey's Uniform Construction Code requires permits for structural masonry work. Cutting into a bearing wall or modifying a load-bearing element requires a permit from the local building department in towns such as Montclair, Wayne, and Morristown. A licensed masonry contractor handles that process. A handyman typically does not."
    ],
    citation: { num: 2, url: "https://imiweb.org", text: "Stone Restoration — International Masonry Institute" }
  },
  {
    icon: Layers,
    title: "Old Mortar Must Be Removed Before New Mortar Bonds to Stone",
    image: stoneMasonryTotowa,
    imageAlt: "Variety of stone masonry patterns in Totowa, Passaic County, NJ — placeholder image",
    content: [
      "Have you ever seen a mortar patch that popped out within a year or two? That usually means someone spread new mortar right over the old. It does not bond that way. The National Park Service recommends removing old mortar to a depth of at least twice the joint width before repointing. That gives the new material enough surface area to grip.",
      "The removal itself matters too. Hand chisels and thin grinder blades work on most joints, but aggressive power tools can chip the edges of softer stone. Brownstone and limestone, which appear on many older homes in Essex County and in Ridgewood, scratch and chip easily if the tool slips.",
      "Once the old mortar is out, the joint gets cleaned of dust and loose material, then dampened. Dry stone pulls water out of fresh mortar too quickly, weakening the bond. A damp surface allows the mortar to cure at the right rate, which is what gives you a joint that lasts 30 to 50 years."
    ],
    citation: { num: 1, url: "https://www.nps.gov/orgs/1739/upload/preservation-brief-02-repointing.pdf", text: "Preservation Brief 2 (U.S. National Park Service)" }
  },
  {
    icon: Shield,
    title: "Stone Masonry Repair Restores Walls, Steps, and Facades to Solid Condition",
    isCallout: true,
    calloutItems: [
      "Repointing: removing and replacing worn mortar joints",
      "Dutchman repair: fitting a thin matching stone over a damaged face",
      "Crack pinning: stainless steel anchors with color-matched composite mortar",
      "Full stone replacement: only when the unit is split through or has lost too much material",
      "Drainage correction: adding weep holes or channels to prevent water pressure rebuild",
    ],
    content: [
      "Most stone repair falls into one of a few categories. Repointing is the most common. That is removing and replacing worn mortar joints. Dutchman repair is for stones that have a damaged face but are still structurally sound. A thin section of matching stone gets cut and fitted over the damaged area. It looks clean and avoids pulling the entire stone out of the wall.",
      "For cracked stone steps, we usually pin the crack with stainless steel anchors and fill with a color-matched composite mortar. Full stone replacement is required only when the unit is split through or has lost too much material to anchor.",
      "North Jersey's clay-heavy soils in Clifton, Little Falls, and Totowa create ground movement that stresses stone walls and steps over time. Wyckoff and Glen Rock see similar issues on sloped lots. Proper repair accounts for that movement. Sometimes a repair includes adding a drainage channel or weep holes so water pressure does not rebuild behind the wall and cause the same failure again."
    ],
    citation: { num: 10, url: "https://www.masoncontractors.org", text: "Masonry Repair and Restoration — MCAA" }
  },
  {
    icon: Thermometer,
    title: "Proper Mortar Match and Joint Tooling Protect Your Stone for Decades",
    image: stoneWorkTotowa,
    imageAlt: "Masonry contractor performing stone work on a residential exterior in Totowa, Passaic County, NJ — placeholder image",
    content: [
      "You can tell a lot about a repair job by looking at the mortar joints. If the new mortar is a different shade or the joint profile does not match the rest of the wall, it most likely indicates an issue during planning.",
      "ASTM C270 sets the standards for mortar used in unit masonry. For most residential stone in North Jersey, Type N mortar is the standard. It has sufficient flexibility to accommodate thermal expansion without cracking. Type O is even softer and works better on historic brownstone and limestone. Using a harder mortar, such as Type S or Type M, on softer stone is a mistake that becomes apparent within a few years as new cracks in the stone itself.",
      "Joint tooling is the finishing step. The profile of the joint, whether it is concave, flush, or raked, affects how water runs off the surface. A concave joint sheds water most effectively and holds up the longest in our climate. Historic districts in Montclair, Ridgewood, and Morristown may require the original joint profile to be matched for preservation compliance."
    ],
    citation: { num: 4, url: "https://www.astm.org", text: "ASTM C270 — Standard Specification for Mortar for Unit Masonry" }
  },
  {
    icon: Search,
    title: "Seasonal Inspections Stop Small Stone Damage From Becoming Costly Repairs",
    image: heroImage,
    imageAlt: "Stone masonry steps with flagstone treads in Verona, Essex County, NJ, by Masonry Pro Construction",
    content: [
      "A quick walk around your property in the spring can save you thousands down the road. After a North Jersey winter, check stone walls and steps for new cracks, loose stones, or mortar that crumbles when you press on it. Those are signs that freeze-thaw did its work over the cold months.",
      "Salt and de-icing agents used across Bergen, Passaic, and Morris Counties speed up mortar breakdown on steps and walkways. If your front steps or walkway are regularly salted, expect the mortar joints in those areas to wear faster than the rest of your stonework.",
      "Look at your chimney too. It takes the most weather exposure of any stone feature on the house. And honestly, most homeowners never look at it until something falls off or a leak shows up inside. A yearly check from ground level with binoculars can catch issues early. If you see anything crumbling or shifted, give us a call or text and we will come out for a closer look."
    ],
    citation: { num: 7, url: "https://www.naturalstoneinstitute.org", text: "Care and Cleaning for Natural Stone — Natural Stone Institute" }
  },
];

const processSteps = [
  "Inspect the stone wall for cracking, spalling, and mortar loss.",
  "Identify the stone type and original mortar composition.",
  "Remove deteriorated mortar to a depth of at least twice the joint width.",
  "Clean joints of dust and debris, then dampen surfaces.",
  "Pack new mortar matched to original strength and color in thin layers.",
  "Tool joints to match the existing profile.",
  "Keep repaired area moist for at least 72 hours to cure properly.",
];

const faqs = [
  {
    question: "How do I know if my stone wall in North Jersey needs repointing or full repair?",
    answer: "If mortar has eroded more than a quarter inch deep, stones are loose, or cracks run through the joints, you likely need more than surface patching. Efflorescence and water staining are early warning signs. A free on-site assessment from our team will tell you exactly what the wall needs and whether repointing alone will handle it or if stones need to be reset or replaced."
  },
  {
    question: "Can you put new mortar on top of old mortar on a stone wall?",
    answer: "No. Old mortar must be removed to at least twice the joint width so the new mortar bonds properly. Layering new over old creates a weak connection that cracks and falls out quickly, often within one or two winters in North Jersey."
  },
  {
    question: "What type of mortar do North Jersey masons use for stone repair?",
    answer: "Type N or Type O mortar is standard for most residential stone repair. These softer mortars flex with temperature changes instead of cracking. Harder mortars like Type S or Type M can damage softer stone like brownstone and limestone, which are common on older homes in this area."
  },
  {
    question: "Do I need a permit for stone masonry repair in North Jersey?",
    answer: "Repointing and surface repairs typically do not require a permit under New Jersey's Uniform Construction Code. Structural work that involves cutting into walls, modifying bearing elements, or rebuilding sections does require a permit through your local building department."
  },
  {
    question: "How long does professional stone masonry repair last?",
    answer: "Properly done repointing lasts 30 to 50 years when the correct mortar type and joint preparation are used. The biggest factor is whether the old mortar was fully removed before the new mortar went in. Shortcuts in prep cut the lifespan by half or more."
  },
  {
    question: "When is the best time of year to schedule stone repair in North Jersey?",
    answer: "Spring through fall is the best window because mortar needs temperatures above 40 degrees to cure correctly. That said, we handle emergency repairs year-round. Winter work is possible with heated enclosures, but scheduling during warmer months gives the mortar the best conditions for a long-lasting bond."
  },
];

const citations = [
  { num: 1, text: "Preservation Brief 2: Repointing Mortar Joints in Historic Masonry Buildings — U.S. National Park Service", url: "https://www.nps.gov/orgs/1739/upload/preservation-brief-02-repointing.pdf" },
  { num: 2, text: "Stone Restoration — International Masonry Institute (IMI)", url: "https://imiweb.org" },
  { num: 3, text: "Restoration Best Practices — International Masonry Institute (IMI)", url: "https://imiweb.org" },
  { num: 4, text: "ASTM C270: Standard Specification for Mortar for Unit Masonry — ASTM International", url: "https://www.astm.org" },
  { num: 5, text: "ASTM C1713: Specification for Mortars for Repair of Historic Masonry — ASTM International", url: "https://www.astm.org" },
  { num: 6, text: "Stone Restoration and Maintenance — Natural Stone Institute", url: "https://www.naturalstoneinstitute.org" },
  { num: 7, text: "Care and Cleaning for Natural Stone — Natural Stone Institute", url: "https://www.naturalstoneinstitute.org" },
  { num: 8, text: "Technical Notes: Maintenance of Brick Masonry (TN 46) — Brick Industry Association (BIA)", url: "https://www.gobrick.com" },
  { num: 9, text: "Repointing (Tuckpointing) Brick Masonry Brief — Brick Industry Association (BIA)", url: "https://www.gobrick.com" },
  { num: 10, text: "Masonry Repair and Restoration Techniques (Education) — Mason Contractors Association of America (MCAA)", url: "https://www.masoncontractors.org" },
  { num: 11, text: "Significant Changes to Minor Work and Ordinary Maintenance — New Jersey Dept. of Community Affairs", url: "https://www.nj.gov/dca/divisions/codes" },
  { num: 12, text: "Construction Permit Application & Forms — New Jersey DCA", url: "https://www.nj.gov/dca/divisions/codes" },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

export default function StoneMasonryRepair() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Stone Masonry Repair Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Cracked mortar or loose stone? We repair walls, steps, and veneers across Bergen, Passaic, Morris, and Essex Counties. Call for a free on-site assessment." />
        <meta property="og:description" content="Cracked mortar or loose stone? We repair walls, steps, and veneers across Bergen, Passaic, Morris, and Essex Counties. Call for a free on-site assessment." />
        <meta property="og:title" content="Stone Masonry Repair Totowa, NJ | Masonry Pro Construction" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Stone Masonry Repair",
            description: "Stone masonry repair for walls, steps, and veneers across Bergen, Passaic, Morris, and Essex Counties.",
            url: "/masonry-services/stone-masonry-repair",
            image: "https://masonry-pro.com/og/stone-masonry-repair.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/stone-masonry-repair.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/stone-masonry-repair.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Stone masonry repair in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Stone Masonry Repair in Totowa, NJ Serving <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>North Jersey</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  North Jersey's freeze-thaw cycles crack mortar and split stone faster than most homeowners expect. This page covers stone masonry repair for walls, chimneys, steps, foundations, and veneers. We offer year-round scheduling, on-site assessments, and same-week availability for urgent repairs. Fully insured masonry contractors trained in both traditional and modern stone repair methods.
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="#contact">Get a Free Quote</a>
                </Button>
              </div>
            </div>
          </section>

          {/* Content Sections */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="space-y-16 max-w-5xl mx-auto">
                {contentSections.map((section, index) => (
                  <div key={section.title} className="grid md:grid-cols-2 gap-8 items-start">
                    {/* Text Content - alternating sides */}
                    <div className={index % 2 === 1 ? "md:order-2" : ""}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-lg bg-gradient-navy flex items-center justify-center">
                          <section.icon className="h-6 w-6 text-secondary" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold">{section.title}</h2>
                      </div>
                      <div className="space-y-4 text-muted-foreground">
                        {section.content.map((paragraph, pIndex) => (
                          <p key={pIndex}>
                            {pIndex === 0 && section.citation ? (
                              <>
                                {paragraph.split('.')[0]}.
                                <CitationLink num={section.citation.num} url={section.citation.url}>
                                  {" "}{section.citation.text}
                                </CitationLink>
                                {paragraph.includes('.') ? paragraph.substring(paragraph.indexOf('.') + 1) : ''}
                              </>
                            ) : (
                              paragraph
                            )}
                          {index === 0 && pIndex === 0 && (<> We've been repairing stonework as a <Link to="/" className="text-secondary hover:underline">masonry contractor serving North Jersey</Link> for 35+ years.</>)}
                          </p>
                        ))}
                      </div>
                    </div>
                    
                    {/* Image or Callout */}
                    <div className={index % 2 === 1 ? "md:order-1" : ""}>
                      {section.isCallout ? (
                        <div className="rounded-xl border-2 border-secondary/30 bg-card p-8">
                          <h3 className="text-xl font-bold mb-4 text-secondary">Common Stone Masonry Repairs</h3>
                          <ul className="space-y-3">
                            {section.calloutItems?.map((item, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="w-2 h-2 rounded-full bg-secondary mt-2 shrink-0" />
                                <span className="text-muted-foreground">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                          <img
                            src={section.image}
                            alt={section.imageAlt}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Process Steps */}
          <section className="py-16 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                  How Do You Repair Damaged Stone Masonry in <span className="text-gradient-gold">North Jersey?</span>
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {processSteps.map((step, index) => (
                    <div key={index} className="bg-background rounded-xl p-6 border border-border relative">
                      <div className="w-10 h-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-bold text-lg mb-4">
                        {index + 1}
                      </div>
                      <p className="text-muted-foreground text-sm">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection 
            title="See Our Stone Masonry"
            highlightedTitle="Work in Action"
            videoIds={["-8-f3fV09Lw", "MOXQjyMxrD8", "c_50pnhg30w"]}
          />

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card rounded-xl p-6 border border-border">
                    <h3 className="text-lg font-bold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" />

          {/* CTA Section */}
          <CTASection 
            serviceName="Stone Masonry Repair"
            heading="Ready for Your"
            highlightedHeading="Stone Masonry Repair?"
          />

          {/* Citations */}
          <section className="py-12 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      References & Citations
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-3 text-sm text-muted-foreground">
                        {citations.map((citation) => (
                          <li key={citation.num} className="flex gap-2">
                            <span className="text-secondary font-bold">[{citation.num}]</span>
                            <span>{citation.text}</span>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
